import API_BASE from "../../utils/api";
import React, { useState, useEffect } from "react";
import axios from "axios";
import Sidebar from "../../components/Sidebar";
import ProfileDropdown from "../../components/ProfileDropdown";
import Modal from "../../components/Modal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { computedAcademicYearFallback } from "../../utils/academicYear";

const FEE_TYPES = ["tuition", "transport", "library", "sports", "hostel", "exam", "development", "miscellaneous", "other"];
const CYCLES = [
  { value: "monthly", label: "Monthly (12 installments)" },
  { value: "quarterly", label: "Quarterly (4 installments)" },
  { value: "half_yearly", label: "Half-Yearly (2 installments)" },
  { value: "yearly", label: "Yearly (1 installment)" },
  { value: "one_time", label: "One-Time" },
];

const emptyComponent = () => ({ label: "", feeType: "tuition", amount: "", cycle: "monthly", dueDay: 5 });
const emptyForm = () => ({ name: "", academicYear: "", classId: "", feeGroup: "", components: [emptyComponent()] });

const FeeStructures = () => {
  const [structures, setStructures] = useState([]);
  const [classes, setClasses] = useState([]);
  const [feeGroups, setFeeGroups] = useState([]);
  // Seeded with the computed fallback for the very first paint; replaced by
  // the school's configured Settings → Academic Year as soon as it loads.
  const [defaultAcademicYear, setDefaultAcademicYear] = useState(computedAcademicYearFallback());
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Generate flow
  const [generateTarget, setGenerateTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [focusStudentId, setFocusStudentId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);
  const [confirmGenerate, setConfirmGenerate] = useState(false);

  const fetchStructures = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/fee-structures`);
      setStructures(res.data);
    } catch (_) {
      setStatus({ type: "error", message: "Failed to load fee structures" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStructures();
    axios.get(`${API_BASE}/classes`).then((res) => setClasses(res.data)).catch(() => {});
    axios.get(`${API_BASE}/settings`).then((res) => {
      setFeeGroups(res.data?.feeGroups || []);
      if (res.data?.academicYear) setDefaultAcademicYear(res.data.academicYear);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (status.message) {
      const t = setTimeout(() => setStatus({ type: "", message: "" }), 3000);
      return () => clearTimeout(t);
    }
  }, [status]);

  const openAdd = () => { setEditing(null); setForm({ ...emptyForm(), academicYear: defaultAcademicYear }); setError(""); setShowModal(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name,
      academicYear: s.academicYear,
      classId: s.classId?._id || "",
      feeGroup: s.feeGroup || "",
      components: s.components.map((c) => ({ ...c, amount: String(c.amount) })),
    });
    setError("");
    setShowModal(true);
  };

  const updateComponent = (i, field, value) => {
    setForm((f) => {
      const components = [...f.components];
      components[i] = { ...components[i], [field]: value };
      return { ...f, components };
    });
  };
  const addComponent = () => setForm((f) => ({ ...f, components: [...f.components, emptyComponent()] }));
  const removeComponent = (i) => setForm((f) => ({ ...f, components: f.components.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    setError("");
    if (!form.name.trim()) return setError("Name is required");
    if (!form.academicYear.trim()) return setError("Academic year is required (e.g. 2026-2027)");
    if (!form.components.length || form.components.some((c) => !c.label.trim() || !c.amount)) {
      return setError("Every component needs a label and amount");
    }

    const payload = {
      name: form.name,
      academicYear: form.academicYear,
      classId: form.classId || null,
      feeGroup: form.feeGroup,
      components: form.components.map((c) => ({ ...c, amount: Number(c.amount), dueDay: Number(c.dueDay) || 5 })),
    };
    try {
      if (editing) {
        await axios.put(`${API_BASE}/fee-structures/${editing._id}`, payload);
        setStatus({ type: "success", message: "Fee structure updated" });
      } else {
        await axios.post(`${API_BASE}/fee-structures`, payload);
        setStatus({ type: "success", message: "Fee structure created" });
      }
      setShowModal(false);
      fetchStructures();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save fee structure");
    }
  };

  const toggleActive = async (s) => {
    try {
      await axios.put(`${API_BASE}/fee-structures/${s._id}`, { isActive: !s.isActive });
      fetchStructures();
    } catch (_) {
      setStatus({ type: "error", message: "Failed to update fee structure" });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_BASE}/fee-structures/${deleteTarget._id}`);
      setStatus({ type: "success", message: "Fee structure deleted" });
      setDeleteTarget(null);
      fetchStructures();
    } catch (_) {
      setStatus({ type: "error", message: "Failed to delete fee structure" });
      setDeleteTarget(null);
    }
  };

  const openGenerate = async (s) => {
    setGenerateTarget(s);
    setPreview(null);
    setGenerateResult(null);
    setFocusStudentId("");
    setPreviewLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/fee-structures/${s._id}/preview`);
      setPreview(res.data);
    } catch (err) {
      setStatus({ type: "error", message: err.response?.data?.error || "Failed to load preview" });
      setGenerateTarget(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const expandStudent = async (studentId) => {
    setFocusStudentId(studentId);
    try {
      const res = await axios.post(`${API_BASE}/fee-structures/${generateTarget._id}/preview?studentId=${studentId}`);
      setPreview(res.data);
    } catch (_) {}
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API_BASE}/fee-structures/${generateTarget._id}/generate`);
      setGenerateResult(res.data);
      setConfirmGenerate(false);
    } catch (err) {
      setStatus({ type: "error", message: err.response?.data?.error || "Failed to generate fees" });
      setConfirmGenerate(false);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">Fee Structures</h1>
            <ProfileDropdown />
          </div>
        </nav>

        {status.message && (
          <div className={`fixed top-4 right-4 z-[100] max-w-sm p-4 rounded-lg shadow-lg ${status.type === "success" ? "bg-green-100 text-green-700 border border-green-400" : "bg-red-100 text-red-700 border border-red-400"}`}>
            {status.message}
          </div>
        )}

        <div className="p-6">
          <div className="bg-white rounded-lg shadow p-6 mb-6 flex justify-between items-center flex-wrap gap-3">
            <div>
              <h2 className="text-lg font-semibold">Configure once, bill correctly all year</h2>
              <p className="text-sm text-gray-500">
                Define a fee plan (components + billing cycle) for a class/fee-group, then Generate to create every student's installments for the year.
              </p>
            </div>
            <button onClick={openAdd} className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition flex items-center">
              <span className="mr-2">+</span> Add Fee Structure
            </button>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Academic Year</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Scope</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Components</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">Loading…</td></tr>
                  ) : structures.length === 0 ? (
                    <tr><td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      <div className="text-4xl mb-2">💰</div>
                      <p>No fee structures yet</p>
                      <button onClick={openAdd} className="mt-3 text-indigo-600 hover:text-indigo-800 text-sm">Create your first fee structure</button>
                    </td></tr>
                  ) : (
                    structures.map((s) => (
                      <tr key={s._id} className="hover:bg-gray-50">
                        <td className="px-5 py-4 font-medium text-gray-800">{s.name}</td>
                        <td className="px-5 py-4 text-sm text-gray-600">{s.academicYear}</td>
                        <td className="px-5 py-4 text-sm text-gray-600">
                          {s.classId ? `Class ${s.classId.className}-${s.classId.section}` : "All Classes"}
                          {s.feeGroup && ` · ${s.feeGroup}`}
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-600">{s.components.length} component(s)</td>
                        <td className="px-5 py-4">
                          <button
                            onClick={() => toggleActive(s)}
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                          >
                            {s.isActive ? "Active" : "Inactive"}
                          </button>
                        </td>
                        <td className="px-5 py-4 text-center whitespace-nowrap">
                          <button onClick={() => openGenerate(s)} className="text-indigo-600 hover:underline text-sm mr-3">Generate</button>
                          <button onClick={() => openEdit(s)} className="text-amber-600 hover:underline text-sm mr-3">Edit</button>
                          <button onClick={() => setDeleteTarget(s)} className="text-red-600 hover:underline text-sm">Delete</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <Modal
          title={editing ? "Edit Fee Structure" : "Add Fee Structure"}
          size="lg"
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={handleSave} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">Save</button>
            </>
          }
        >
          <div className="space-y-4">
            {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Standard Day Scholar Plan" className="w-full border rounded-md px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Academic Year *</label>
                <input type="text" value={form.academicYear} onChange={(e) => setForm({ ...form, academicYear: e.target.value })} placeholder="2026-2027" className="w-full border rounded-md px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
                <select value={form.classId} onChange={(e) => setForm({ ...form, classId: e.target.value })} className="w-full border rounded-md px-3 py-2">
                  <option value="">All Classes</option>
                  {classes.map((c) => <option key={c._id} value={c._id}>Class {c.className} - Section {c.section}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fee Group</label>
                <select value={form.feeGroup} onChange={(e) => setForm({ ...form, feeGroup: e.target.value })} className="w-full border rounded-md px-3 py-2">
                  <option value="">All Fee Groups</option>
                  {feeGroups.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-gray-700">Fee Components</p>
                <button type="button" onClick={addComponent} className="text-sm text-indigo-600 hover:underline">+ Add Component</button>
              </div>
              <div className="space-y-2">
                {form.components.map((c, i) => (
                  <div key={i} className="grid grid-cols-1 sm:grid-cols-6 gap-2 items-end bg-gray-50 rounded-lg p-3">
                    <div className="sm:col-span-2">
                      <label className="block text-xs text-gray-500 mb-1">Label</label>
                      <input type="text" value={c.label} onChange={(e) => updateComponent(i, "label", e.target.value)} placeholder="Tuition Fee" className="w-full border rounded-md px-2 py-1.5 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Fee Type</label>
                      <select value={c.feeType} onChange={(e) => updateComponent(i, "feeType", e.target.value)} className="w-full border rounded-md px-2 py-1.5 text-sm capitalize">
                        {FEE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Amount / Cycle (Rs.)</label>
                      <input type="number" min="0" value={c.amount} onChange={(e) => updateComponent(i, "amount", e.target.value)} className="w-full border rounded-md px-2 py-1.5 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Cycle</label>
                      <select value={c.cycle} onChange={(e) => updateComponent(i, "cycle", e.target.value)} className="w-full border rounded-md px-2 py-1.5 text-sm">
                        {CYCLES.map((cy) => <option key={cy.value} value={cy.value}>{cy.label}</option>)}
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">Due Day</label>
                        <input type="number" min="1" max="31" value={c.dueDay} onChange={(e) => updateComponent(i, "dueDay", e.target.value)} className="w-full border rounded-md px-2 py-1.5 text-sm" />
                      </div>
                      {form.components.length > 1 && (
                        <button type="button" onClick={() => removeComponent(i)} className="text-red-500 text-xs hover:underline pb-2">Remove</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {generateTarget && (
        <Modal
          title={`Generate: ${generateTarget.name}`}
          subtitle={generateResult ? "Done" : "Review before committing — nothing is saved until you confirm"}
          size="xl"
          onClose={() => setGenerateTarget(null)}
          footer={
            !generateResult && (
              <>
                <button onClick={() => setGenerateTarget(null)} className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100">Cancel</button>
                <button
                  onClick={() => setConfirmGenerate(true)}
                  disabled={previewLoading || !preview?.matchingStudentCount}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  Generate for {preview?.matchingStudentCount || 0} Student(s)
                </button>
              </>
            )
          }
        >
          {generateResult ? (
            <div className="text-center py-6">
              <div className="text-4xl mb-3">✅</div>
              <p className="text-lg font-semibold text-gray-800">
                Created {generateResult.created} installment(s) for {generateResult.studentCount} student(s)
              </p>
              {generateResult.skipped > 0 && (
                <p className="text-sm text-gray-500 mt-1">{generateResult.skipped} already existed and were skipped (safe to re-run).</p>
              )}
            </div>
          ) : previewLoading ? (
            <p className="text-sm text-gray-400 text-center py-8">Loading preview…</p>
          ) : preview ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Matches <strong>{preview.matchingStudentCount}</strong> active student(s). Click a row to see per-installment detail.
              </p>
              <div className="overflow-x-auto border rounded-lg max-h-80 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Roll No</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500 uppercase">Installments</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Gross</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Discount</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Net</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {preview.summary.map((row) => (
                      <React.Fragment key={row.studentId}>
                        <tr onClick={() => expandStudent(row.studentId)} className="cursor-pointer hover:bg-indigo-50">
                          <td className="px-4 py-2">{row.rollNumber}</td>
                          <td className="px-4 py-2 font-medium">{row.name}</td>
                          <td className="px-4 py-2 text-center">{row.installmentCount}</td>
                          <td className="px-4 py-2 text-right">Rs. {row.grossAmount.toLocaleString()}</td>
                          <td className="px-4 py-2 text-right text-green-600">{row.discountAmount > 0 ? `- Rs. ${row.discountAmount.toLocaleString()}` : "—"}</td>
                          <td className="px-4 py-2 text-right font-semibold">Rs. {row.netAmount.toLocaleString()}</td>
                        </tr>
                        {focusStudentId === row.studentId && preview.focusDetail && (
                          <tr>
                            <td colSpan="6" className="px-4 py-3 bg-gray-50">
                              <div className="space-y-2">
                                {preview.focusDetail.components.map((comp) => (
                                  <div key={comp.key}>
                                    <p className="text-xs font-semibold text-gray-700">
                                      {comp.label} — Rs. {comp.netAmount.toLocaleString()}/installment
                                      {comp.discountAmount > 0 && ` (Rs. ${comp.discountAmount.toLocaleString()} off: ${comp.discountReason})`}
                                    </p>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {comp.installments.map((inst) => (
                                        <span key={inst.period} className="px-2 py-0.5 bg-white border rounded text-xs text-gray-600">
                                          {inst.period}: {new Date(inst.dueDate).toLocaleDateString("en-IN")}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </Modal>
      )}

      {confirmGenerate && (
        <ConfirmDialog
          title="Confirm Generate"
          message={`This will create fee installments for ${preview?.matchingStudentCount || 0} student(s). Already-generated periods are skipped automatically, so this is safe to run again later.`}
          confirmLabel={generating ? "Generating…" : "Generate"}
          onConfirm={handleGenerate}
          onCancel={() => setConfirmGenerate(false)}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete Fee Structure"
          message={`Delete "${deleteTarget.name}"? Installments already generated from it will not be removed.`}
          confirmLabel="Delete"
          danger
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default FeeStructures;
